import { t } from '@viral-space/shared';
import {
  COLORS, FONT, GLOW, el,
  injectSharedKeyframes, attachMobileCloseButton,
  isMobileLayout,
} from './ui-shared';
import {
  getInputSettings,
  setInputSettings,
  MIN_SENSITIVITY,
  MAX_SENSITIVITY,
  type InputSettings,
} from '../input/settings';
import { createLanguageSelector } from './language-selector';
import { isElectronDesktop } from '../input/mobile';

// ── Display settings helpers (desktop only) ───────────────────────────────
// Preferences are mirrored in localStorage so they survive across sessions.
// The IPC bridge applies them to the BrowserWindow immediately.
const DISPLAY_PREFS_KEY = 'zerion:displayPrefs';

interface DisplayPrefs {
  fullscreen: boolean;
  resolution: [number, number] | null; // null = use whatever the window currently is
}

function loadDisplayPrefs(): DisplayPrefs {
  try {
    const raw = localStorage.getItem(DISPLAY_PREFS_KEY);
    if (raw) return JSON.parse(raw) as DisplayPrefs;
  } catch { /* ignore */ }
  return { fullscreen: true, resolution: null };
}

function saveDisplayPrefs(prefs: DisplayPrefs): void {
  try { localStorage.setItem(DISPLAY_PREFS_KEY, JSON.stringify(prefs)); } catch { /* ignore */ }
}

// Common 16:9 preset sizes offered in the resolution picker.
const RESOLUTION_PRESETS: Array<[number, number]> = [
  [1280, 720],
  [1366, 768],
  [1600, 900],
  [1920, 1080],
  [2560, 1440],
  [3840, 2160],
];

export type PauseAction =
  | 'resume'
  | 'controls'
  | 'leaderboard'
  | 'friends'
  | 'missions'
  | 'charSelect'
  | 'signOut'
  | 'deleteAccount'
  | 'upgrade';

/**
 * In-place toggle (e.g. music on/off). Unlike a `PauseAction`, activating
 * a toggle re-renders the menu rather than closing it, so the user can
 * see the new state immediately.
 */
export interface PauseToggle {
  isEnabled: () => boolean;
  toggle: () => void;
}

export interface PauseMenuOptions {
  music?: PauseToggle;
  /**
   * Premium subscription presence callback. When the function returns
   * `false` the pause menu surfaces an "UNLOCK PREMIUM" CTA at the top
   * of the General category (highlighted in secondary colour). When it
   * returns `true` the CTA is replaced by a quiet "PREMIUM ACTIVE"
   * badge so the player can still see the state without being nagged.
   * Implemented as a callback (not a static boolean) so the menu
   * always reads the live value at open time — important because the
   * server can flip the bit mid-session via webhook push.
   */
  isPremium?: () => boolean;
}

type CategoryId =
  | 'general'
  | 'audio'
  | 'controls'
  | 'display'
  | 'missions'
  | 'leaderboard'
  | 'friends';

interface Category {
  id: CategoryId;
  labelKey: string;
  icon: string;
  /**
   * Categories above the divider are configuration / actions you stay
   * inside the pause menu for. Categories below it ("missions",
   * "leaderboard", "friends") are jump targets — selecting them shows
   * an explanatory pane with an OPEN button so the player can confirm
   * they want to leave the options screen.
   */
  group: 'settings' | 'shortcut';
}

// 'display' is injected at runtime only when running inside the Electron shell.
const BASE_CATEGORIES: Category[] = [
  { id: 'general',     labelKey: 'options.cat.general',     icon: '⏻', group: 'settings' },
  { id: 'audio',       labelKey: 'options.cat.audio',       icon: '♪', group: 'settings' },
  { id: 'controls',    labelKey: 'options.cat.controls',    icon: '⌨', group: 'settings' },
  { id: 'missions',    labelKey: 'options.cat.missions',    icon: '✦', group: 'shortcut' },
  { id: 'leaderboard', labelKey: 'options.cat.leaderboard', icon: '♛', group: 'shortcut' },
  { id: 'friends',     labelKey: 'options.cat.friends',     icon: '☻', group: 'shortcut' },
];

function getCategories(): Category[] {
  if (!isElectronDesktop()) return BASE_CATEGORIES;
  // Insert 'display' after 'controls', before the shortcut group.
  return [
    BASE_CATEGORIES[0],
    BASE_CATEGORIES[1],
    BASE_CATEGORIES[2],
    { id: 'display', labelKey: 'options.cat.display', icon: '▣', group: 'settings' },
    ...BASE_CATEGORIES.slice(3),
  ];
}

export class PauseMenu {
  private container: HTMLElement;
  private root: HTMLElement;
  private backdrop: HTMLElement;
  private panel: HTMLElement;
  private body: HTMLElement;
  private sidebar: HTMLElement;
  private content: HTMLElement;
  private visible = false;
  private onAction: (action: PauseAction) => void;
  private readonly onKeyDown: (e: KeyboardEvent) => void;
  private options: PauseMenuOptions;
  private activeCategory: CategoryId = 'general';
  /**
   * Per-category sidebar handles so we can repaint the "selected"
   * highlight without rebuilding the whole sidebar (which would steal
   * focus from any input the player may have tapped on the right).
   */
  private categoryButtons: Map<CategoryId, HTMLElement> = new Map();

  constructor(
    container: HTMLElement,
    onAction: (action: PauseAction) => void,
    options: PauseMenuOptions = {},
  ) {
    this.container = container;
    this.onAction = onAction;
    this.options = options;
    injectSharedKeyframes();

    this.root = el('div', {
      position: 'absolute',
      inset: '0',
      zIndex: '300',
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
    });
    container.appendChild(this.root);

    this.backdrop = el('div', {
      position: 'absolute',
      inset: '0',
      background: 'rgba(0,4,0,0.75)',
      backdropFilter: 'blur(4px)',
      transition: 'opacity 0.25s ease',
      opacity: '0',
    });
    this.backdrop.addEventListener('click', () => this.doAction('resume'));
    this.root.appendChild(this.backdrop);

    // Full-screen modal on every viewport so the new categorized layout
    // has room for a sidebar of categories + a right pane of content.
    // Desktop caps the width so it doesn't span an ultrawide; mobile
    // fills the entire safe-area rectangle so the option list is never
    // hidden behind notches or the home indicator.
    const mobile = isMobileLayout();
    this.panel = el('div', {
      position: 'relative',
      zIndex: '1',
      display: 'flex',
      flexDirection: 'column',
      background: 'rgba(0,10,0,0.94)',
      border: `1px solid ${COLORS.dim}`,
      boxShadow: `0 0 40px rgba(0,255,65,0.12), inset 0 0 30px rgba(0,255,65,0.03)`,
      opacity: '0',
      transform: 'scale(0.96)',
      transition: 'opacity 0.25s ease, transform 0.25s ease',
      // Mobile: fill the entire viewport (minus the safe-area inset)
      // so the sidebar + content stack works without any cropping.
      // Desktop: a sensible max width / height so the modal doesn't
      // monopolize a 4K screen but is still big enough to surface the
      // category sidebar comfortably.
      width: mobile ? '100vw' : 'min(820px, 92vw)',
      height: mobile ? '100vh' : 'min(560px, 88vh)',
      maxWidth: mobile ? '100vw' : '92vw',
      maxHeight: mobile ? '100vh' : '88vh',
      paddingTop: mobile ? 'env(safe-area-inset-top, 0px)' : '',
      paddingRight: mobile ? 'env(safe-area-inset-right, 0px)' : '',
      paddingBottom: mobile ? 'env(safe-area-inset-bottom, 0px)' : '',
      paddingLeft: mobile ? 'env(safe-area-inset-left, 0px)' : '',
      boxSizing: 'border-box',
      overflow: 'hidden',
    });
    this.root.appendChild(this.panel);

    attachMobileCloseButton(this.panel, () => this.doAction('resume'));

    // body / sidebar / content are populated by buildContent() the
    // first time the menu opens — they get rebuilt on every open so a
    // viewport rotation between opens picks up the right desktop /
    // mobile flexDirection. Stub them with empty divs here so the
    // class invariants (`!: HTMLElement` definite assignment) hold.
    this.body = el('div', {});
    this.sidebar = el('div', {});
    this.content = el('div', {});

    this.onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Escape' && this.visible) {
        e.preventDefault();
        e.stopPropagation();
        this.doAction('resume');
      }
    };

    window.addEventListener('keydown', this.onKeyDown, true);
  }

  private doAction(action: PauseAction): void {
    this.hide();
    this.onAction(action);
  }

  private buildContent(): void {
    this.panel.innerHTML = '';
    this.categoryButtons.clear();

    // Re-derive the mobile flag here too — the constructor's binding
    // is out of scope and `buildContent` can be re-invoked on toggle
    // / locale change after a viewport rotation crossed the breakpoint.
    const mobile = isMobileLayout();

    const header = el('div', {
      width: '100%',
      textAlign: 'center',
      padding: mobile ? '14px 24px 10px' : '20px 24px 16px',
      fontFamily: FONT,
      fontSize: 'clamp(16px, 2.5vw, 22px)',
      color: COLORS.primary,
      textShadow: GLOW,
      letterSpacing: '6px',
      textTransform: 'uppercase',
      borderBottom: `1px solid ${COLORS.dim}`,
      flexShrink: '0',
    }, t('pause.title'));
    this.panel.appendChild(header);

    // Body wraps the sidebar + content. Mobile flips it to vertical
    // stack: tabs on top, content fills the remaining space.
    this.body = el('div', {
      flex: '1',
      display: 'flex',
      flexDirection: mobile ? 'column' : 'row',
      minHeight: '0',
      overflow: 'hidden',
    });
    this.panel.appendChild(this.body);

    this.buildSidebar(mobile);
    this.buildContentPane(mobile);

    const footer = el('div', {
      width: '100%',
      textAlign: 'center',
      padding: mobile ? '6px' : '8px',
      fontSize: '8px',
      color: COLORS.dim,
      opacity: '0.4',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      fontFamily: FONT,
      borderTop: `1px solid ${COLORS.dim}`,
      flexShrink: '0',
    }, 'ZERION: INFINITE SPACE');
    this.panel.appendChild(footer);
  }

  private buildSidebar(mobile: boolean): void {
    this.sidebar = el('div', {
      display: 'flex',
      flexDirection: mobile ? 'row' : 'column',
      // Mobile tabs scroll horizontally so adding more categories later
      // stays graceful on a 320px-wide phone in landscape.
      overflowX: mobile ? 'auto' : 'hidden',
      overflowY: mobile ? 'hidden' : 'auto',
      flexShrink: '0',
      gap: mobile ? '4px' : '0',
      padding: mobile ? '8px 8px' : '12px 0',
      width: mobile ? '100%' : 'clamp(180px, 24%, 220px)',
      borderRight: mobile ? '' : `1px solid ${COLORS.dim}`,
      borderBottom: mobile ? `1px solid ${COLORS.dim}` : '',
      background: 'rgba(0,16,0,0.4)',
      boxSizing: 'border-box',
    });
    this.body.appendChild(this.sidebar);

    let lastGroup: Category['group'] | null = null;
    for (const cat of getCategories()) {
      // Insert a divider row between configuration categories
      // ("settings") and the jump-target categories ("shortcut") so
      // the player visually distinguishes "things I tweak" from
      // "places I open". Skip the divider on mobile (tabs are
      // horizontal — a vertical line would look out of place).
      if (!mobile && lastGroup === 'settings' && cat.group === 'shortcut') {
        const sep = el('div', {
          height: '1px',
          background: COLORS.dim,
          opacity: '0.4',
          margin: '6px 16px',
        });
        this.sidebar.appendChild(sep);
      }
      lastGroup = cat.group;
      this.sidebar.appendChild(this.makeCategoryButton(cat, mobile));
    }
  }

  private makeCategoryButton(cat: Category, mobile: boolean): HTMLElement {
    const isActive = this.activeCategory === cat.id;
    const isShortcut = cat.group === 'shortcut';
    const baseColor = isShortcut ? COLORS.dimText : COLORS.primary;

    const btn = el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: mobile ? '6px' : '10px',
      padding: mobile ? '8px 12px' : '10px 18px',
      cursor: 'pointer',
      fontFamily: FONT,
      fontSize: mobile ? '11px' : '12px',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      color: baseColor,
      transition: 'all 0.15s',
      pointerEvents: 'auto',
      flexShrink: '0',
      borderLeft: mobile ? '' : `3px solid ${isActive ? COLORS.primary : 'transparent'}`,
      borderBottom: mobile ? `2px solid ${isActive ? COLORS.primary : 'transparent'}` : '',
      background: isActive ? 'rgba(0,255,65,0.08)' : 'transparent',
      whiteSpace: 'nowrap',
    });

    const iconEl = el('span', {
      fontSize: mobile ? '13px' : '15px',
      width: mobile ? 'auto' : '18px',
      textAlign: 'center',
      opacity: isActive ? '1' : '0.7',
    }, cat.icon);
    btn.appendChild(iconEl);

    const labelEl = el('span', {
      opacity: isActive ? '1' : '0.85',
    }, t(cat.labelKey));
    btn.appendChild(labelEl);

    btn.addEventListener('mouseenter', () => {
      if (this.activeCategory !== cat.id) {
        btn.style.background = 'rgba(0,255,65,0.05)';
      }
    });
    btn.addEventListener('mouseleave', () => {
      if (this.activeCategory !== cat.id) {
        btn.style.background = 'transparent';
      }
    });
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.selectCategory(cat.id);
    });

    this.categoryButtons.set(cat.id, btn);
    return btn;
  }

  private selectCategory(id: CategoryId): void {
    if (this.activeCategory === id) return;
    this.activeCategory = id;
    // Repaint sidebar buttons in-place so the highlight follows the
    // selection without dropping any focus state on the content side.
    for (const [cid, btn] of this.categoryButtons) {
      const isActive = cid === id;
      const mobile = isMobileLayout();
      btn.style.background = isActive ? 'rgba(0,255,65,0.08)' : 'transparent';
      if (mobile) {
        btn.style.borderBottomColor = isActive ? COLORS.primary : 'transparent';
      } else {
        btn.style.borderLeftColor = isActive ? COLORS.primary : 'transparent';
      }
    }
    this.renderContentPane();
  }

  private buildContentPane(mobile: boolean): void {
    this.content = el('div', {
      flex: '1',
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: mobile ? '14px 16px' : '20px 28px',
      boxSizing: 'border-box',
      minHeight: '0',
    });
    this.body.appendChild(this.content);
    this.renderContentPane();
  }

  private renderContentPane(): void {
    this.content.innerHTML = '';
    switch (this.activeCategory) {
      case 'general':
        this.renderGeneralPane();
        break;
      case 'audio':
        this.renderAudioPane();
        break;
      case 'controls':
        this.renderControlsPane();
        break;
      case 'display':
        this.renderDisplayPane();
        break;
      case 'missions':
        this.renderShortcutPane('missions');
        break;
      case 'leaderboard':
        this.renderShortcutPane('leaderboard');
        break;
      case 'friends':
        this.renderShortcutPane('friends');
        break;
    }
  }

  /**
   * Generic action row (icon + label + optional trailing hint) used
   * inside category panes. Matches the desktop look the previous
   * pause menu used for its single flat list.
   */
  private makeActionRow(opts: {
    icon: string;
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'ghost' | 'danger' | 'upgrade';
    trailing?: string;
  }): HTMLElement {
    const variant = opts.variant ?? 'ghost';
    const color =
      variant === 'danger'
        ? COLORS.danger
        : variant === 'upgrade'
          ? COLORS.secondary
          : COLORS.primary;

    const row = el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 14px',
      marginBottom: '6px',
      fontFamily: FONT,
      fontSize: variant === 'primary' ? '14px' : '12px',
      color,
      letterSpacing: '3px',
      textTransform: 'uppercase',
      cursor: 'pointer',
      transition: 'all 0.15s',
      pointerEvents: 'auto',
      border: `1px solid ${variant === 'upgrade' ? 'rgba(255,176,0,0.4)' : COLORS.dim}`,
      background: variant === 'upgrade' ? 'rgba(255,176,0,0.05)' : 'transparent',
    });

    const iconEl = el('span', {
      fontSize: '18px',
      width: '24px',
      textAlign: 'center',
      opacity: '0.85',
    }, opts.icon);
    row.appendChild(iconEl);

    const labelEl = el('span', { flex: '1' }, opts.label);
    row.appendChild(labelEl);

    if (opts.trailing) {
      const t = el('span', {
        marginLeft: 'auto',
        fontSize: '9px',
        color: COLORS.dim,
        letterSpacing: '1px',
      }, opts.trailing);
      row.appendChild(t);
    }

    row.addEventListener('mouseenter', () => {
      row.style.background = variant === 'upgrade'
        ? 'rgba(255,176,0,0.16)'
        : 'rgba(0,255,65,0.08)';
      row.style.boxShadow = `0 0 8px rgba(0,255,65,0.18)`;
    });
    row.addEventListener('mouseleave', () => {
      row.style.background = variant === 'upgrade'
        ? 'rgba(255,176,0,0.05)'
        : 'transparent';
      row.style.boxShadow = '';
    });
    row.addEventListener('click', (e) => {
      e.stopPropagation();
      opts.onClick();
    });

    return row;
  }

  /**
   * Toggle row used by every "X: ON / OFF" preference (music, invert
   * Y, …). Keeps the visual style aligned with `makeActionRow` so the
   * pane reads as a coherent list.
   */
  private makeToggleRow(opts: {
    icon: string;
    label: string;
    enabled: boolean;
    onToggle: () => void;
    description?: string;
  }): HTMLElement {
    const wrap = el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      padding: '12px 14px',
      marginBottom: '6px',
      fontFamily: FONT,
      fontSize: '12px',
      color: COLORS.primary,
      letterSpacing: '3px',
      textTransform: 'uppercase',
      cursor: 'pointer',
      transition: 'all 0.15s',
      pointerEvents: 'auto',
      border: `1px solid ${COLORS.dim}`,
      background: 'transparent',
    });

    const top = el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    });

    const iconEl = el('span', {
      fontSize: '18px',
      width: '24px',
      textAlign: 'center',
      opacity: opts.enabled ? '1' : '0.55',
    }, opts.icon);
    top.appendChild(iconEl);

    const labelEl = el('span', {
      flex: '1',
      opacity: opts.enabled ? '1' : '0.65',
    }, opts.label);
    top.appendChild(labelEl);

    const stateEl = el('span', {
      fontSize: '11px',
      letterSpacing: '2px',
      color: opts.enabled ? COLORS.primary : COLORS.dim,
      textShadow: opts.enabled ? GLOW : '',
      padding: '2px 10px',
      border: `1px solid ${opts.enabled ? COLORS.primary : COLORS.dim}`,
      background: opts.enabled ? 'rgba(0,255,65,0.12)' : 'transparent',
      minWidth: '42px',
      textAlign: 'center',
    }, opts.enabled ? t('options.toggle.on') : t('options.toggle.off'));
    top.appendChild(stateEl);

    wrap.appendChild(top);

    if (opts.description) {
      const desc = el('div', {
        marginLeft: '36px',
        fontSize: '9px',
        color: COLORS.dim,
        letterSpacing: '1px',
        textTransform: 'none',
        opacity: '0.7',
        lineHeight: '1.4',
      }, opts.description);
      wrap.appendChild(desc);
    }

    wrap.addEventListener('mouseenter', () => {
      wrap.style.background = 'rgba(0,255,65,0.06)';
      wrap.style.boxShadow = `0 0 8px rgba(0,255,65,0.18)`;
    });
    wrap.addEventListener('mouseleave', () => {
      wrap.style.background = 'transparent';
      wrap.style.boxShadow = '';
    });
    wrap.addEventListener('click', (e) => {
      e.stopPropagation();
      opts.onToggle();
    });

    return wrap;
  }

  /**
   * Numeric slider row used by sensitivity preferences. Built with a
   * native `<input type="range">` so the player gets the familiar OS
   * thumb + keyboard arrow steps for fine adjustment. The trailing
   * label shows the current multiplier rounded to 2 decimals so a
   * value of `0.7` reads as "0.70×" — the `×` glyph hints that the
   * number is a multiplier on the stock feel, not an absolute speed.
   *
   * `onChange` fires on every `input` event, so the new value is
   * persisted live while the player drags (lets them feel the change
   * mid-drag without having to release first). The row also rebuilds
   * the trailing label on every drag so the readout stays accurate.
   */
  private makeSliderRow(opts: {
    icon: string;
    label: string;
    value: number;
    min: number;
    max: number;
    step: number;
    onChange: (next: number) => void;
    description?: string;
  }): HTMLElement {
    const wrap = el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      padding: '12px 14px',
      marginBottom: '6px',
      fontFamily: FONT,
      fontSize: '12px',
      color: COLORS.primary,
      letterSpacing: '3px',
      textTransform: 'uppercase',
      pointerEvents: 'auto',
      border: `1px solid ${COLORS.dim}`,
      background: 'transparent',
    });

    const top = el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    });

    const iconEl = el('span', {
      fontSize: '18px',
      width: '24px',
      textAlign: 'center',
      opacity: '0.85',
    }, opts.icon);
    top.appendChild(iconEl);

    const labelEl = el('span', { flex: '1' }, opts.label);
    top.appendChild(labelEl);

    const readout = el('span', {
      fontSize: '11px',
      letterSpacing: '1px',
      color: COLORS.primary,
      textShadow: GLOW,
      padding: '2px 10px',
      border: `1px solid ${COLORS.primary}`,
      background: 'rgba(0,255,65,0.08)',
      minWidth: '52px',
      textAlign: 'center',
    }, `${opts.value.toFixed(2)}\u00D7`);
    top.appendChild(readout);

    wrap.appendChild(top);

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = String(opts.min);
    slider.max = String(opts.max);
    slider.step = String(opts.step);
    slider.value = String(opts.value);
    Object.assign(slider.style, {
      width: '100%',
      marginLeft: '36px',
      maxWidth: 'calc(100% - 36px)',
      accentColor: COLORS.primary,
      pointerEvents: 'auto',
      cursor: 'pointer',
    } as Partial<CSSStyleDeclaration>);
    slider.addEventListener('input', () => {
      const v = parseFloat(slider.value);
      if (!Number.isFinite(v)) return;
      readout.textContent = `${v.toFixed(2)}\u00D7`;
      opts.onChange(v);
    });
    slider.addEventListener('click', (e) => e.stopPropagation());
    wrap.appendChild(slider);

    if (opts.description) {
      const desc = el('div', {
        marginLeft: '36px',
        fontSize: '9px',
        color: COLORS.dim,
        letterSpacing: '1px',
        textTransform: 'none',
        opacity: '0.7',
        lineHeight: '1.4',
      }, opts.description);
      wrap.appendChild(desc);
    }

    return wrap;
  }

  /**
   * Section heading (small dim caption) printed above a group of rows
   * inside a category pane. Helps when a single category contains
   * unrelated subgroups (e.g. Controls → invert axis vs help shortcut).
   */
  private makeSectionLabel(text: string): HTMLElement {
    return el('div', {
      fontFamily: FONT,
      fontSize: '10px',
      color: COLORS.dim,
      letterSpacing: '3px',
      textTransform: 'uppercase',
      margin: '4px 4px 8px',
      opacity: '0.85',
    }, text);
  }

  private renderGeneralPane(): void {
    this.content.appendChild(
      this.makeActionRow({
        icon: '▶',
        label: t('pause.resume'),
        variant: 'primary',
        trailing: 'ESC',
        onClick: () => this.doAction('resume'),
      }),
    );

    if (this.options.isPremium) {
      if (!this.options.isPremium()) {
        this.content.appendChild(
          this.makeActionRow({
            icon: '★',
            label: t('pause.upgrade'),
            variant: 'upgrade',
            onClick: () => this.doAction('upgrade'),
          }),
        );
      } else {
        const badge = el('div', {
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '10px 14px',
          marginBottom: '6px',
          fontFamily: FONT,
          fontSize: '11px',
          color: COLORS.secondary,
          letterSpacing: '3px',
          textTransform: 'uppercase',
          border: `1px solid rgba(255,176,0,0.4)`,
          background: 'rgba(255,176,0,0.05)',
        });
        badge.appendChild(el('span', { fontSize: '16px', opacity: '0.85' }, '★'));
        badge.appendChild(el('span', { flex: '1' }, t('pause.premiumActive')));
        this.content.appendChild(badge);
      }
    }

    this.content.appendChild(this.makeLanguageRow());

    this.content.appendChild(
      this.makeActionRow({
        icon: '⇄',
        label: t('pause.changePilot'),
        onClick: () => this.doAction('charSelect'),
      }),
    );
    this.content.appendChild(
      this.makeActionRow({
        icon: '⏻',
        label: t('pause.signOut'),
        variant: 'danger',
        onClick: () => this.doAction('signOut'),
      }),
    );

    // Permanent account deletion (App Store guideline 5.1.1(v)). Gated
    // behind an explicit irreversible-action confirmation dialog so a
    // stray tap can never wipe a pilot's progress.
    this.content.appendChild(
      this.makeActionRow({
        icon: '⊗',
        label: t('pause.deleteAccount'),
        variant: 'danger',
        onClick: () => this.showDeleteAccountConfirm(),
      }),
    );
  }

  /**
   * Two-step confirmation gate for permanent account deletion. Renders a
   * modal over the pause panel spelling out that the action is
   * irreversible; only an explicit tap on the destructive button fires
   * the `deleteAccount` pause action. Cancelling (button, backdrop, or
   * Escape) simply tears the overlay down and leaves the player in the
   * options menu.
   */
  private showDeleteAccountConfirm(): void {
    const overlay = el('div', {
      position: 'absolute',
      inset: '0',
      zIndex: '10',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0,4,0,0.8)',
      backdropFilter: 'blur(3px)',
      padding: '24px',
      boxSizing: 'border-box',
    });

    const dialog = el('div', {
      width: 'min(420px, 100%)',
      maxHeight: '100%',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      padding: '24px',
      background: 'rgba(10,0,0,0.96)',
      border: `1px solid ${COLORS.danger}`,
      boxShadow: '0 0 30px rgba(255,60,60,0.25)',
      fontFamily: FONT,
    });

    const title = el('div', {
      fontSize: 'clamp(15px, 2.4vw, 18px)',
      color: COLORS.danger,
      letterSpacing: '3px',
      textTransform: 'uppercase',
      textAlign: 'center',
    }, t('account.delete.title'));
    dialog.appendChild(title);

    const body = el('div', {
      fontSize: '12px',
      lineHeight: '1.6',
      color: COLORS.dimText,
      letterSpacing: '0.5px',
      textAlign: 'center',
    }, t('account.delete.warning'));
    dialog.appendChild(body);

    const buttons = el('div', {
      display: 'flex',
      gap: '12px',
      marginTop: '4px',
    });

    const cancelBtn = el('div', {
      flex: '1',
      padding: '12px',
      textAlign: 'center',
      fontFamily: FONT,
      fontSize: '12px',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      color: COLORS.primary,
      border: `1px solid ${COLORS.dim}`,
      cursor: 'pointer',
      pointerEvents: 'auto',
      transition: 'all 0.15s',
    }, t('account.delete.cancel'));
    cancelBtn.addEventListener('mouseenter', () => {
      cancelBtn.style.background = 'rgba(0,255,65,0.08)';
    });
    cancelBtn.addEventListener('mouseleave', () => {
      cancelBtn.style.background = 'transparent';
    });

    const confirmBtn = el('div', {
      flex: '1',
      padding: '12px',
      textAlign: 'center',
      fontFamily: FONT,
      fontSize: '12px',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      color: COLORS.danger,
      border: `1px solid ${COLORS.danger}`,
      background: 'rgba(255,60,60,0.08)',
      cursor: 'pointer',
      pointerEvents: 'auto',
      transition: 'all 0.15s',
    }, t('account.delete.confirm'));
    confirmBtn.addEventListener('mouseenter', () => {
      confirmBtn.style.background = 'rgba(255,60,60,0.2)';
    });
    confirmBtn.addEventListener('mouseleave', () => {
      confirmBtn.style.background = 'rgba(255,60,60,0.08)';
    });

    const close = (): void => {
      overlay.remove();
    };
    cancelBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      close();
    });
    confirmBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      close();
      this.doAction('deleteAccount');
    });
    // Tapping the dim backdrop (but not the dialog itself) cancels.
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });

    buttons.appendChild(cancelBtn);
    buttons.appendChild(confirmBtn);
    dialog.appendChild(buttons);
    overlay.appendChild(dialog);
    this.panel.appendChild(overlay);
  }

  /**
   * Inline language picker for the General pane. Built as a custom row
   * (rather than `makeActionRow` / `makeToggleRow`) because the trailing
   * dropdown needs to capture clicks without bubbling up to the row's
   * own click handler — there is no row-level action here, only the
   * select itself. Switching the language re-renders the pane in place
   * so every label across the modal flips to the new locale immediately
   * without the player having to close and reopen the options window.
   */
  private makeLanguageRow(): HTMLElement {
    const row = el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '10px 14px',
      marginBottom: '6px',
      fontFamily: FONT,
      fontSize: '12px',
      color: COLORS.primary,
      letterSpacing: '3px',
      textTransform: 'uppercase',
      border: `1px solid ${COLORS.dim}`,
      background: 'transparent',
    });

    row.appendChild(el('span', {
      fontSize: '18px',
      width: '24px',
      textAlign: 'center',
      opacity: '0.85',
    }, '🌐'));

    row.appendChild(el('span', { flex: '1' }, t('menu.language')));

    const selector = createLanguageSelector({
      showLabel: false,
      onChange: () => {
        // Repaint everything so headers, sidebar labels, action rows,
        // and the freshly-mounted dropdown's own option captions all
        // pick up the new locale. We rebuild rather than re-render only
        // the content pane because the sidebar buttons read `t()` too.
        this.buildContent();
      },
    });
    selector.style.marginLeft = 'auto';
    selector.addEventListener('click', (e) => e.stopPropagation());
    row.appendChild(selector);

    return row;
  }

  private renderAudioPane(): void {
    this.content.appendChild(this.makeSectionLabel(t('options.cat.audio')));
    if (this.options.music) {
      const ctrl = this.options.music;
      const enabled = ctrl.isEnabled();
      this.content.appendChild(
        this.makeToggleRow({
          icon: '♪',
          label: t('options.audio.music'),
          enabled,
          onToggle: () => {
            ctrl.toggle();
            if (this.visible) this.renderContentPane();
          },
          description: t('options.audio.musicDesc'),
        }),
      );
    } else {
      this.content.appendChild(this.makePlaceholder(t('options.audio.noOptions')));
    }
  }

  private renderControlsPane(): void {
    const settings: InputSettings = getInputSettings();
    const mobile = isMobileLayout();

    // Desktop: surface the existing keyboard reference as a quick action
    // so legacy `H` / "show controls" muscle memory still has a button.
    // Mobile: hidden — the keyboard chart isn't actionable on a touch
    // device, exactly the rationale that already lives in the previous
    // pause menu code.
    if (!mobile) {
      this.content.appendChild(this.makeSectionLabel(t('options.controls.helpSection')));
      this.content.appendChild(
        this.makeActionRow({
          icon: '⌨',
          label: t('pause.controls'),
          trailing: 'H',
          onClick: () => this.doAction('controls'),
        }),
      );
    }

    this.content.appendChild(this.makeSectionLabel(t('options.controls.aimingSection')));

    // Show only the invert-Y toggle + sensitivity slider that actually
    // drive the current device's primary input surface — there's no
    // point showing a touch-stick toggle on a desktop with no touch
    // screen, and vice versa. The flags remain independent in storage
    // (so a Surface / iPad-with-mouse pilot keeps their per-input
    // preference even when their layout swaps), but only the live ones
    // are presented. We deliberately do NOT re-render the entire pane
    // after a slider drag — that would steal focus from the thumb /
    // arrow keys mid-adjustment; the slider keeps its own state and
    // pushes updates to storage live.
    if (mobile) {
      this.content.appendChild(
        this.makeToggleRow({
          icon: '↕',
          label: t('options.controls.invertY'),
          enabled: settings.invertYTouch,
          onToggle: () => {
            setInputSettings({ invertYTouch: !settings.invertYTouch });
            this.renderContentPane();
          },
          description: t('options.controls.invertYTouchDesc'),
        }),
      );
      this.content.appendChild(
        this.makeSliderRow({
          icon: '◎',
          label: t('options.controls.sensitivity'),
          value: settings.touchSensitivity,
          min: MIN_SENSITIVITY,
          max: MAX_SENSITIVITY,
          step: 0.05,
          onChange: (v) => {
            setInputSettings({ touchSensitivity: v });
          },
          description: t('options.controls.touchSensitivityDesc'),
        }),
      );
    } else {
      this.content.appendChild(
        this.makeToggleRow({
          icon: '↕',
          label: t('options.controls.invertY'),
          enabled: settings.invertYMouse,
          onToggle: () => {
            setInputSettings({ invertYMouse: !settings.invertYMouse });
            this.renderContentPane();
          },
          description: t('options.controls.invertYMouseDesc'),
        }),
      );
      this.content.appendChild(
        this.makeSliderRow({
          icon: '◎',
          label: t('options.controls.sensitivity'),
          value: settings.mouseSensitivity,
          min: MIN_SENSITIVITY,
          max: MAX_SENSITIVITY,
          step: 0.05,
          onChange: (v) => {
            setInputSettings({ mouseSensitivity: v });
          },
          description: t('options.controls.mouseSensitivityDesc'),
        }),
      );
    }
  }

  /**
   * Display settings pane — shown only when running inside the Electron /
   * Steam desktop shell (isElectronDesktop() === true). Lets the player
   * toggle borderless fullscreen and pick a windowed resolution from a
   * list of common presets. Both settings are persisted in localStorage and
   * applied immediately via the window IPC bridge in the preload script.
   *
   * The pane is async-aware: it queries the current window state via IPC on
   * every open so the readout is always accurate (e.g. the user may have
   * used the OS resize handle since the last time the menu was open).
   */
  private renderDisplayPane(): void {
    this.content.appendChild(this.makeSectionLabel(t('options.cat.display')));

    const winBridge = (window as Window).zerionDesktop?.window;
    if (!winBridge) {
      this.content.appendChild(this.makePlaceholder('Display controls unavailable'));
      return;
    }

    const prefs = loadDisplayPrefs();

    // ── Fullscreen toggle ─────────────────────────────────────────────────
    // We render a placeholder row immediately and then replace it with the
    // live state once the IPC round-trip resolves (~1 frame). This avoids
    // a noticeable layout shift while keeping the pane layout stable.
    const fsSlot = el('div', {});
    this.content.appendChild(fsSlot);

    const buildFsRow = (isFs: boolean): HTMLElement => {
      return this.makeToggleRow({
        icon: '⛶',
        label: t('options.display.fullscreen'),
        enabled: isFs,
        onToggle: async () => {
          const next = !isFs;
          await winBridge.setFullscreen(next);
          saveDisplayPrefs({ ...prefs, fullscreen: next });
          // Re-render so the toggle badge flips immediately.
          if (this.visible) this.renderContentPane();
        },
        description: t('options.display.fullscreenDesc'),
      });
    };

    winBridge.isFullscreen().then((isFs) => {
      fsSlot.replaceWith(buildFsRow(isFs));
    }).catch(() => {
      fsSlot.replaceWith(buildFsRow(prefs.fullscreen));
    });

    // ── Resolution picker ─────────────────────────────────────────────────
    this.content.appendChild(this.makeSectionLabel(t('options.display.resolution')));

    const resRow = el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      padding: '12px 14px',
      marginBottom: '6px',
      fontFamily: FONT,
      fontSize: '12px',
      color: COLORS.primary,
      letterSpacing: '3px',
      textTransform: 'uppercase',
      border: `1px solid ${COLORS.dim}`,
      background: 'transparent',
    });

    const resTop = el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    });
    resTop.appendChild(el('span', {
      fontSize: '18px',
      width: '24px',
      textAlign: 'center',
      opacity: '0.85',
    }, '▣'));
    resTop.appendChild(el('span', { flex: '1' }, t('options.display.resolution')));

    // Native <select> styled to match the game's monospace aesthetic.
    const select = document.createElement('select');
    Object.assign(select.style, {
      background: 'rgba(0,16,0,0.9)',
      color: COLORS.primary,
      border: `1px solid ${COLORS.primary}`,
      fontFamily: FONT,
      fontSize: '11px',
      letterSpacing: '1px',
      padding: '3px 8px',
      cursor: 'pointer',
      pointerEvents: 'auto',
      outline: 'none',
      marginLeft: 'auto',
    } as Partial<CSSStyleDeclaration>);

    let currentRes: [number, number] = prefs.resolution ?? [1600, 900];
    for (const [w, h] of RESOLUTION_PRESETS) {
      const opt = document.createElement('option');
      opt.value = `${w}x${h}`;
      opt.textContent = `${w} × ${h}`;
      if (currentRes[0] === w && currentRes[1] === h) opt.selected = true;
      select.appendChild(opt);
    }

    // Populate with live window size once IPC resolves.
    winBridge.getResolution().then(([w, h]) => {
      currentRes = [w, h];
      const matchOpt = select.querySelector<HTMLOptionElement>(`option[value="${w}x${h}"]`);
      if (matchOpt) matchOpt.selected = true;
    }).catch(() => { /* keep default */ });

    select.addEventListener('change', async (e) => {
      e.stopPropagation();
      const [w, h] = (select.value).split('x').map(Number) as [number, number];
      await winBridge.setResolution(w, h);
      saveDisplayPrefs({ ...prefs, fullscreen: false, resolution: [w, h] });
      // After setResolution the window exits fullscreen — reflect that in
      // the rendered toggle without full pane re-render (avoids interrupting
      // the select interaction).
    });
    select.addEventListener('click', (e) => e.stopPropagation());

    resTop.appendChild(select);
    resRow.appendChild(resTop);

    const resDesc = el('div', {
      marginLeft: '36px',
      fontSize: '9px',
      color: COLORS.dim,
      letterSpacing: '1px',
      textTransform: 'none',
      opacity: '0.7',
      lineHeight: '1.4',
    }, t('options.display.resolutionDesc'));
    resRow.appendChild(resDesc);

    this.content.appendChild(resRow);
  }

  private renderShortcutPane(target: 'missions' | 'leaderboard' | 'friends'): void {
    const labelKey =
      target === 'missions' ? 'options.cat.missions'
      : target === 'leaderboard' ? 'options.cat.leaderboard'
      : 'options.cat.friends';
    const descKey =
      target === 'missions' ? 'options.shortcut.missionsDesc'
      : target === 'leaderboard' ? 'options.shortcut.leaderboardDesc'
      : 'options.shortcut.friendsDesc';
    const icon =
      target === 'missions' ? '✦'
      : target === 'leaderboard' ? '♛'
      : '☻';
    const action: PauseAction = target;

    const wrap = el('div', {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '14px',
      padding: '8px 4px',
    });

    const title = el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      fontFamily: FONT,
      fontSize: '15px',
      color: COLORS.primary,
      letterSpacing: '4px',
      textTransform: 'uppercase',
      textShadow: GLOW,
    });
    title.appendChild(el('span', { fontSize: '20px' }, icon));
    title.appendChild(el('span', {}, t(labelKey)));
    wrap.appendChild(title);

    const desc = el('div', {
      fontFamily: FONT,
      fontSize: '11px',
      color: COLORS.dim,
      letterSpacing: '1px',
      textTransform: 'none',
      lineHeight: '1.6',
      opacity: '0.85',
      maxWidth: '460px',
    }, t(descKey));
    wrap.appendChild(desc);

    wrap.appendChild(
      this.makeActionRow({
        icon: '▶',
        label: t('options.shortcut.open'),
        variant: 'primary',
        onClick: () => this.doAction(action),
      }),
    );

    this.content.appendChild(wrap);
  }

  private makePlaceholder(text: string): HTMLElement {
    return el('div', {
      fontFamily: FONT,
      fontSize: '11px',
      color: COLORS.dim,
      letterSpacing: '2px',
      textAlign: 'center',
      padding: '40px 16px',
      opacity: '0.7',
    }, text);
  }

  show(): void {
    if (this.visible) return;
    this.visible = true;
    // Reset to General every time the menu opens so a player who jumped
    // into Controls last session doesn't land on a settings-only pane
    // after pressing ESC just to "go back to game".
    this.activeCategory = 'general';
    this.buildContent();
    this.root.style.display = 'flex';
    requestAnimationFrame(() => {
      this.backdrop.style.opacity = '1';
      this.panel.style.opacity = '1';
      this.panel.style.transform = 'scale(1)';
    });
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.backdrop.style.opacity = '0';
    this.panel.style.opacity = '0';
    this.panel.style.transform = 'scale(0.96)';
    setTimeout(() => {
      if (!this.visible) this.root.style.display = 'none';
    }, 250);
  }

  toggle(): void {
    if (this.visible) this.hide();
    else this.show();
  }

  isVisible(): boolean {
    return this.visible;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown, true);
    this.root.remove();
  }
}
